import * as React from "react";
import { cn } from "@/lib/utils";

// MUI's Snackbar, for the two places the pane uses one: a message floated over the
// foot of the pane that can hide itself after a delay. Not a toast queue -- nothing in
// the pane shows two at once, and a library for that would be a dependency for a div.

interface SnackbarProps {
  open: boolean;
  /** Called once `autoHideDuration` ms have passed since it opened. */
  onClose?: () => void;
  autoHideDuration?: number;
  /** Where along the foot. The pane is narrow, so "center" spans it with a margin. */
  align?: "center" | "end";
  className?: string;
  children: React.ReactNode;
}

const Snackbar = ({
  open,
  onClose,
  autoHideDuration,
  align = "center",
  className,
  children,
}: SnackbarProps) => {
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  React.useEffect(() => {
    if (!open || autoHideDuration === undefined) return undefined;
    const timer = window.setTimeout(() => onCloseRef.current?.(), autoHideDuration);
    return () => window.clearTimeout(timer);
  }, [open, autoHideDuration]);

  if (!open) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "fixed bottom-2.5 z-50 animate-in fade-in-0 slide-in-from-bottom-2",
        align === "center" ? "inset-x-2.5" : "right-2.5 max-w-[calc(100%-20px)]",
        className
      )}
    >
      {children}
    </div>
  );
};

export { Snackbar };
